import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import type { StaticModelCollection } from '../firestore-mobx/model'
import { fetchFeaturedSlideDecks } from '../firestore/SlideDeck'
import {
  buildEmptySlideDeckMaterialCollection,
  fetchFeaturedSlideDeckMaterials,
} from '../firestore/SlideDeckMaterial'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { SlideDeck } from '../models/SlideDeck'
import type { SlideDeckMaterial } from '../models/SlideDeckMaterial'
import { Cubit } from './core'
import type { HttpsCallable } from 'firebase/functions'
import { httpsCallable } from 'firebase/functions'

type ConnectDifferentProviderAccountFunction = HttpsCallable<
  {
    token: string
    email: string
  },
  | {
      success: true
      customToken?: string
    }
  | {
      success: false
    }
>

export type LoginCubitInvitationState =
  | { loading: true; hasInvitationId?: never; result?: never }
  | {
      loading: false
      hasInvitationId: false
      result?: never
    }
  | {
      loading: false
      hasInvitationId: true
      result: Awaited<ReturnType<FirebaseRepository['checkIsValidInvitation']>>
    }

export class LoginCubit extends Cubit {
  repository: FirebaseRepository

  slideDecks: StaticModelCollection<SlideDeck>
  slideDeckMaterials = observable.map<
    string,
    StaticModelCollection<SlideDeckMaterial>
  >()

  slideDecksInitialized = false
  private _currentInvitationToken: string | null = null

  @observable invitationState: LoginCubitInvitationState = { loading: true }

  connectDifferentProviderAccountFunction: ConnectDifferentProviderAccountFunction

  constructor(repository: FirebaseRepository) {
    super()
    this.repository = repository

    this.slideDecks = SlideDeck.emptyCollection(repository)

    makeObservable(this)

    this.connectDifferentProviderAccountFunction = httpsCallable<
      {
        token: string
        email: string
      },
      | {
          success: true
          customToken?: string
        }
      | {
          success: false
        }
    >(this.repository.functions, 'connectDifferentProviderAccount')
  }

  @computed
  get slideDecksWithMaterials() {
    const array: {
      slideDeck: SlideDeck
      material: SlideDeckMaterial
    }[] = []

    for (const slideDeck of this.slideDecks.models) {
      const materials = this.slideDeckMaterials.get(slideDeck.id)
      if (!materials || !materials.length) continue
      array.push({ slideDeck, material: materials.models[0] })
    }

    return array
  }

  // only run once
  async initSlideDecks() {
    if (this.slideDecksInitialized) return
    this.slideDecksInitialized = true

    const newSlideDecks = await fetchFeaturedSlideDecks(this.repository)

    // randomized array of featured slide decks with name and image
    // We only run it once, because we want that set to stay the same
    // for the duration of the visit on the page
    const filtered = newSlideDecks
      .filter((slideDeck) => {
        return slideDeck.data.slideDeckImageURL && slideDeck.data.slideDeckName
      })
      .sort(() => Math.random() - 0.5)
      .slice(0, 5)

    this.slideDecks.replaceModels(filtered)

    const promises: Promise<void>[] = []
    for (const slideDeck of filtered) {
      promises.push(this.addSlideDeckMaterials(slideDeck.id))
    }

    await Promise.all(promises)
  }

  async addSlideDeckMaterials(slideDeckId: string) {
    const collection = buildEmptySlideDeckMaterialCollection(this.repository)
    this.slideDeckMaterials.set(slideDeckId, collection)

    const slideDeckMaterials = await fetchFeaturedSlideDeckMaterials(
      this.repository,
      { slideDeckId }
    )

    collection.replaceModels(slideDeckMaterials)
  }

  @action
  async checkInvitationForCurrentToken(token: string | null) {
    // Reset to loading state
    this.invitationState = { loading: true }

    if (!this.repository.featureFlags.data.verifyInvitation)
      return (this.invitationState = {
        loading: false,
        hasInvitationId: false,
      })

    if (!token)
      return (this.invitationState = {
        loading: false,
        hasInvitationId: false,
      })

    // don't re-check the same token
    if (token === this._currentInvitationToken) return

    this._currentInvitationToken = token

    try {
      // Check the token
      const result = await this.repository.checkIsValidInvitation(token)

      // make sure the token hasn't changed while we were awaiting
      // i.e path changed and we are validating a different token instead
      if (token !== this._currentInvitationToken) return

      runInAction(() => {
        this.invitationState = {
          loading: false,
          hasInvitationId: true,
          result,
        }
      })
    } catch (e) {
      // You may want to import captureException here or handle errors differently
      console.error('Failed to check invitation:', e)
      if (token !== this._currentInvitationToken) return
      runInAction(() => {
        this.invitationState = {
          loading: false,
          hasInvitationId: false,
        }
      })
    }
  }

  @computed
  get invitationData() {
    const state = this.invitationState

    if (state.loading) {
      return {
        viewToShow: 'loading' as const,
        title: '',
        subtitle: '',
        dataForValidInvitationType: null,
      }
    }

    if (!state.hasInvitationId) {
      return null // Let the component handle the default login view
    }

    const result = state.result

    // Handle invitation-specific logic here
    if (result.isValid && result.invitationType === 'section') {
      return {
        viewToShow: 'sectionInvitation' as const,
        section: result.section,
        instructor: result.instructor,
      }
    }

    if (result.isValid) {
      const typesWithView: (typeof result.invitationType)[] = [
        'instructor',
        'ta',
      ]
      // valid invitation with a handled view for the type
      if (typesWithView.includes(result.invitationType))
        return {
          viewToShow: 'validInvitation' as const,
          invitationType: result.invitationType,
        }

      // invitation is not invalid, but we have no view to show for it
      // so show the default login view
      return null
    }

    // because the user may be re-consuming a one-time slideDeckReview invitation
    // this is a supported case where we don't explicity error, punt validation to
    // after login
    if (
      state.result.isValid === false &&
      state.result.invalidReason === 'oneTimeInvitationAlreadyConsumed' &&
      state.result.invitationType === 'slideDeckReview'
    )
      return null

    return {
      viewToShow: 'invalidInvitation' as const,
      invalidReason: !result.isValid ? result.invalidReason : 'internal',
    }
  }
}
